import React, { CSSProperties, FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { searchCustomerByMobile, createBeneficiaryApi, createCustomerApi, sendTransactionApi, verifyMpinApi, getActiveBanksApi, getApplicationMessageApi } from "../api/api";

interface Beneficiary {
  beneficiaries_id: number;
  bank_id?: number | null;
  bank_name: string;
  account_holder_name: string;
  ifsc_code: string;
  account_number: string;
  verified: number;
}

interface CustomerResponse {
  customers_id: number;
  retailer_id: number;
  mobile_number: string;
  customer_name: string;
  beneficiaries: Beneficiary[];
}

interface ActiveBankOption {
  bank_id: number;
  bank_name: string;
  ifsc_code: string;
}

const mobileRegex = /^[6-9]\d{9}$/;
const pincodeRegex = /^\d{6}$/;

type SearchState = "idle" | "found" | "notFound";

export default function SendMoneyPage() {
  const { user } = useAuth();

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const [searchState, setSearchState] = useState<SearchState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [appMessage, setAppMessage] = useState<string>("");

  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ mobile_number: "", customer_name: "", pincode: "" });
  const [newBeneficiary, setNewBeneficiary] = useState({ account_holder_name: "", bank_id: "", bank_name: "", ifsc_code: "", account_number: "" });
  const [bankOptions, setBankOptions] = useState<ActiveBankOption[]>([]);
  const [customerFormError, setCustomerFormError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [confirmData, setConfirmData] = useState<{ beneficiary: Beneficiary; amount: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mpinInput, setMpinInput] = useState("");
  const [verifyingMpin, setVerifyingMpin] = useState(false);
  const [mpinError, setMpinError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<Record<string, string> | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState<string | null>(null);
  const receiptRef = useRef<HTMLDivElement | null>(null);

  const resetSearchResult = () => {
    setCustomer(null);
    setSearchState("idle");
    setError(null);
  };

  const searchByMobile = async () => {
    setError(null);
    if (!mobile.trim()) {
      resetSearchResult();
      return setError("Enter mobile number");
    }

    if (!mobileRegex.test(mobile.trim())) {
      resetSearchResult();
      return setError("Invalid mobile number");
    }

    if (!user?.token) {
      resetSearchResult();
      return setError("Not authenticated");
    }

    setLoading(true);
    try {
      const body = await searchCustomerByMobile(mobile.trim(), user.token, user.id);
      console.debug("searchByMobile response:", body);

      const data = (body && body.data) ? (body.data as CustomerResponse) : null;

      // If API returned no body, or indicates failure, or no data — treat as not found
      if (!body || body?.success === false || !data) {
        console.debug("searchByMobile: treating as not found; opening Add Customer modal");
        setCustomer(null);
        setSearchState("notFound");
        setShowAddCustomer(true);
        return setError(body?.message || "Customer not found.");
      }

      setCustomer(data);
      setSearchState("found");
    } catch (err: unknown) {
      setCustomer(null);
      setSearchState("idle");
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMobileKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") searchByMobile();
  };

  const openAddCustomer = () => {
    setError(null);
    setCustomerFormError(null);
    setNewCustomer((c) => ({ ...c, mobile_number: mobile.trim() }));
    setShowAddCustomer(true);
  };

  const submitAddCustomer = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    setCustomerFormError(null);

    if (!user?.token) {
      setCustomerFormError("Not authenticated");
      return;
    }
    if (!mobileRegex.test(newCustomer.mobile_number.trim())) {
      setCustomerFormError("Invalid mobile number");
      return;
    }
    if (!newCustomer.customer_name.trim() || newCustomer.customer_name.trim().length < 5) {
      setCustomerFormError("Customer name must be at least 5 characters");
      return;
    }
    if (!pincodeRegex.test(newCustomer.pincode.trim())) {
      setCustomerFormError("Invalid pincode");
      return;
    }

    setCreatingCustomer(true);
    try {
      const payload = { login_id: String(user.id), mobile_number: newCustomer.mobile_number.trim(), customer_name: newCustomer.customer_name.trim(), pincode: newCustomer.pincode.trim() };
      const body = await createCustomerApi(payload, user.token);
      if (!body?.success) throw new Error(body?.message || "Failed to create customer");
      const created = body.data;
      if (created) {
        setCustomer({ customers_id: Number(created.customers_id), retailer_id: Number(created.retailer_id), mobile_number: created.mobile_number, customer_name: created.customer_name, beneficiaries: [] });
        setSearchState("found");
        setMobile(created.mobile_number);
      }
      setShowAddCustomer(false);
    } catch (err: unknown) {
      setCustomerFormError(err instanceof Error ? err.message : "Failed to create customer");
    } finally {
      setCreatingCustomer(false);
    }
  };

  const fetchActiveBanks = async () => {
    if (!user?.token) return;

    try {
      const body = await getActiveBanksApi(user.token);
      const list = Array.isArray(body?.data) ? body.data : [];
      setBankOptions(list);
    } catch (err) {
      console.error("Failed to load active banks", err);
      setBankOptions([]);
    }
  };

  const submitAddBeneficiary = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (!user?.token) return setError("Not authenticated");
    if (!customer) return setError("Search a customer first");
    if (!newBeneficiary.account_holder_name.trim() || newBeneficiary.account_holder_name.trim().length < 3) return setError("Account holder name is required");
    if (!newBeneficiary.bank_id) return setError("Please select a bank");
    if (!newBeneficiary.ifsc_code.trim()) return setError("IFSC code is required");
    if (!newBeneficiary.account_number.trim() || !/^\d+$/.test(newBeneficiary.account_number.trim())) return setError("Account number must contain only digits");

    setCreating(true);
    try {
      const payload = {
        customer_id: String(customer.customers_id),
        account_holder_name: newBeneficiary.account_holder_name.trim(),
        bank_id: Number(newBeneficiary.bank_id),
        bank_name: newBeneficiary.bank_name.trim(),
        ifsc_code: newBeneficiary.ifsc_code.trim(),
        account_number: newBeneficiary.account_number.trim(),
      };
      const body = await createBeneficiaryApi(payload, user.token);
      if (!body?.success) throw new Error(body?.message || "Failed to create beneficiary");
      if (body.data) setCustomer((p) => (p ? { ...p, beneficiaries: [...p.beneficiaries, body.data] } : p));
      setShowAddBeneficiary(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create beneficiary");
    } finally {
      setCreating(false);
    }
  };

  const handleSendClick = (beneficiary: Beneficiary) => {
    setError(null);
    const amount = amounts[beneficiary.beneficiaries_id];
    if (!amount) return setError("Enter amount to send");
    if (!customer) return setError("Customer missing");
    setConfirmData({ beneficiary, amount });
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    if (!confirmData) return;
    setError(null);
    setMpinError(null);
    if (!mpinInput.trim()) return setMpinError("Enter MPIN to proceed");
    if (!user?.token) return setError("Not authenticated");

    setVerifyingMpin(true);
    try {
      const verifyBody = await verifyMpinApi(user.id, mpinInput.trim(), user.token);
      const verifyStatus = verifyBody?.data?.verify_status ?? false;
      if (!verifyStatus) {
        setMpinError(verifyBody?.data?.message || "Invalid MPIN");
        return;
      }
    } catch (err: unknown) {
      setMpinError(err instanceof Error ? err.message : "MPIN verification failed");
      return;
    } finally {
      setVerifyingMpin(false);
    }

    if (!customer) return setError("Customer missing");

    setShowConfirm(false);
    setSending(true);
    try {
      const payload = {
        beneficiary_id: String(confirmData.beneficiary.beneficiaries_id),
        retailer_id: String(customer.retailer_id),
        user_amount: String(confirmData.amount),
        bank_id: confirmData.beneficiary.bank_id ? String(confirmData.beneficiary.bank_id) : "",
      };
      const body = await sendTransactionApi(payload, user.token);
      const amountValue = parseFloat(confirmData.amount);
      const serviceFee = Number((amountValue * 0.01).toFixed(2));
      const totalPayable = Number((amountValue + serviceFee).toFixed(2));

      if (body?.success) {
        const receipt = {
          "Transaction ID": body?.data?.transaction_id || `TXN${Date.now()}`,
          "Status": "SUCCESS",
          "Amount": `₹${confirmData.amount}`,
          "Beneficiary": confirmData.beneficiary.account_holder_name,
          "Bank": confirmData.beneficiary.bank_name,
          "Account No.": confirmData.beneficiary.account_number,
          "IFSC": confirmData.beneficiary.ifsc_code,
          "Request Amount": `₹${confirmData.amount}`,
          "Service Fee": `₹${body?.data?.service_fee ?? serviceFee}`,
          "Total Payable": `₹${body?.data?.total_payable ?? totalPayable}`,
        };

        setReceiptData(receipt);
        setSuccessMsg(body.message || "Money sent successfully");
        setShowReceipt(true);
        setAmounts((prev) => {
          const updated = { ...prev };
          delete updated[confirmData.beneficiary.beneficiaries_id];
          return updated;
        });
        setConfirmData(null);
        setMpinInput("");
      } else {
        const message = body?.message || "Transaction failed";
        setError(message);
        setErrorPopupMessage(message);
        setShowErrorPopup(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Transaction error";
      setError(message);
      setErrorPopupMessage(message);
      setShowErrorPopup(true);
    } finally {
      setSending(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!receiptRef.current) return;
    try {
      const printWindow = window.open("", "_blank", "width=1000,height=760");
      if (!printWindow) {
        throw new Error("Print window blocked");
      }

      const receiptHtml = receiptRef.current.outerHTML;
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Transaction Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; background: #f9fafb; }
              .receipt-printable { max-width: 920px; margin: 0 auto; }
            </style>
          </head>
          <body>
            ${receiptHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch (pdfError) {
      console.error('PDF download failed', pdfError);
      setErrorPopupMessage('Unable to open print dialog. Please allow popups.');
      setShowErrorPopup(true);
    }
  };

  const handleCancelSend = () => {
    setShowConfirm(false);
    setConfirmData(null);
  };

  useEffect(() => setNewCustomer((p) => ({ ...p, mobile_number: mobile.trim() })), [mobile]);

  useEffect(() => {
    const fetchApplicationMessage = async () => {
      if (!user?.token) return;
      try {
        const body = await getApplicationMessageApi(user.token);
        const message =
          (typeof body?.data?.message === "string" && body.data.message) ||
          (typeof body?.message === "string" && body.message) ||
          (typeof body?.data === "string" && body.data) ||
          "";
        setAppMessage(message);
      } catch (err) {
        setAppMessage("");
      }
    };

    fetchApplicationMessage();
  }, [user?.token]);

  useEffect(() => {
    if (user?.token) {
      fetchActiveBanks();
    }
  }, [user?.token]);

  const formattedAppMessage = appMessage ? appMessage.replace(/,/g, ",\n") : "";

  return (
    <div>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .receipt-printable, .receipt-printable * { visibility: visible !important; }
          .receipt-printable { position: absolute !important; left: 0; top: 0; width: 100% !important; }
          .receipt-no-print { display: none !important; }
        }
      `}</style>
      <h1 style={{ fontSize: 28, marginBottom: 18 }}>Send Money</h1>

      {formattedAppMessage ? (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            borderRadius: 10,
            background: "#fef3c7",
            border: "1px solid #fde68a",
            color: "#92400e",
            fontWeight: 600,
            whiteSpace: "pre-line",
            lineHeight: 1.6,
            width: "40%",
            minWidth: 260,
          }}
        >
          {formattedAppMessage}
        </div>
      ) : null}

      <div style={styles.card}>
        <div style={styles.cardHeader}>Search Customer</div>
        <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center" }}>
          <input type="text" placeholder="Enter mobile number" value={mobile} onChange={(e) => setMobile(e.target.value)} onKeyDown={handleMobileKeyDown} style={{ ...styles.input, ...styles.searchInput }} />
          <button onClick={searchByMobile} disabled={loading} style={styles.primaryBtn}>{loading ? "Searching..." : "Search"}</button>
        </div>

        {searchState === "notFound" && (
          <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ color: "#333" }}>Customer not found.</div>
            <button onClick={openAddCustomer} style={styles.ghostBtn}>+ Add Customer</button>
          </div>
        )}

        {searchState === "found" && customer && (
          <div style={{ marginTop: 20 }}>
            <div style={styles.infoBox}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{customer.customer_name}</div>
              <div style={{ color: "#666", marginTop: 6 }}>{customer.mobile_number}</div>
            </div>

            <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontWeight: 700 }}>Beneficiaries</div>
              <button onClick={() => setShowAddBeneficiary(true)} style={styles.ghostBtn}>+ Add Beneficiary</button>
            </div>

            {customer.beneficiaries.length === 0 ? (
              <div style={{ padding: 16, background: "#f8fafc", borderRadius: 10, marginTop: 12 }}>No beneficiaries added yet.</div>
            ) : (
              <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                {customer.beneficiaries.map((beneficiary) => (
                  <div key={beneficiary.beneficiaries_id} style={{ ...styles.beneficiaryCard, position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{beneficiary.account_holder_name}</div>
                        <div style={{ color: "#666", marginTop: 6 }}>{beneficiary.bank_name} • IFSC: {beneficiary.ifsc_code}</div>
                        <div style={{ color: "#333", marginTop: 6 }}>Account: {beneficiary.account_number}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                        <div style={{ color: beneficiary.verified ? "#2ecc71" : "#f59e0b", fontWeight: 700 }}>{beneficiary.verified ? "Verified" : "Unverified"}</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input type="number" placeholder="Amount" min="1" value={amounts[beneficiary.beneficiaries_id] ?? ""} onChange={(e) => setAmounts((p) => ({ ...p, [beneficiary.beneficiaries_id]: e.target.value }))} style={{ ...styles.input, width: 160 }} />
                          <button onClick={() => handleSendClick(beneficiary)} disabled={sending} style={styles.primaryBtn}>{sending ? "Sending..." : "Send"}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {error && searchState !== "notFound" && <div style={{ color: "#d9534f", marginTop: 16 }}>{error}</div>}
      </div>

      {showConfirm && confirmData && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0 }}>Confirm Transaction</h3>
            <div style={{ marginBottom: 12 }}>
              <div><strong>Beneficiary:</strong> {confirmData.beneficiary.account_holder_name}</div>
              <div><strong>Amount:</strong> ₹{confirmData.amount}</div>
              <div><strong>Bank:</strong> {confirmData.beneficiary.bank_name}</div>
              <div><strong>Account:</strong> {confirmData.beneficiary.account_number}</div>
            </div>
            <div style={{ marginBottom: 12 }}>Are you sure you want to send this amount?</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Enter MPIN to confirm</label>
              <input type="password" placeholder="Enter MPIN" value={mpinInput} onChange={(e) => setMpinInput(e.target.value.replace(/\D/g, ""))} maxLength={6} style={styles.input} />
              {mpinError && <div style={{ color: "#d9534f", marginTop: 8 }}>{mpinError}</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleConfirmSend} disabled={verifyingMpin || sending} style={styles.primaryBtn}>{verifyingMpin || sending ? "Processing..." : "Yes, Send"}</button>
              <button onClick={handleCancelSend} disabled={verifyingMpin || sending} style={styles.ghostBtn}>No, Cancel</button>
            </div>
          </div>
        </div>
      )}

      {successMsg && !showReceipt && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0, color: "#10b981" }}>✓ Success</h3>
            <div style={{ marginBottom: 16, fontSize: 16 }}>{successMsg}</div>
            <button onClick={() => setShowReceipt(true)} style={styles.primaryBtn}>View Receipt</button>
          </div>
        </div>
      )}

      {showErrorPopup && errorPopupMessage && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0, color: "#d9534f" }}>⚠️ Transaction Error</h3>
            <div style={{ marginBottom: 16, fontSize: 16 }}>{errorPopupMessage}</div>
            <button onClick={() => setShowErrorPopup(false)} style={styles.primaryBtn}>OK</button>
          </div>
        </div>
      )}

      {showReceipt && receiptData && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>Transaction Receipt</h3>
                <div style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>Invoice layout matching the DMT receipt design</div>
              </div>
              <button onClick={() => setShowReceipt(false)} style={styles.closeBtn}>✕</button>
            </div>

            <div ref={receiptRef} className="receipt-printable" style={{ marginTop: 18, padding: 24, background: '#f9fafb' }}>
              <div style={{ width: '100%', maxWidth: 860, margin: '0 auto', background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <div style={{ background: '#4f46e5', padding: '14px 20px', color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  Print Money Transfer Receipt
                </div>
                <div style={{ padding: '20px 24px', textAlign: 'center', borderBottom: '1px solid #d1d5db', background: '#f8fafc' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.04em', color: '#111827' }}>DMT RECEIPT</div>
                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: '#111827' }}>#{receiptData['Transaction ID'] || 'N/A'}</div>
                </div>

                <div style={{ padding: '24px 24px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, rowGap: 10, borderBottom: '1px solid #d1d5db' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>Date:</span>
                      <span>{new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>Sender Number:</span>
                      <span>{customer?.mobile_number || '—'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>Account Number:</span>
                      <span>{receiptData['Account No.'] || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>Beneficiary Name:</span>
                      <span>{receiptData['Beneficiary'] || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>IFSC Code:</span>
                      <span>{receiptData['IFSC'] || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151' }}>
                      <span>Transaction Type:</span>
                      <span>REMITTANCE IMPS</span>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '20px 24px 18px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.04em', color: '#111827', marginBottom: 12 }}>Transaction Details:</div>
                  <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #d1d5db' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr', background: '#eef2ff', padding: '14px 16px', fontSize: 11, fontWeight: 700, color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <div>Sr. No.</div>
                      <div>TID</div>
                      <div>Amount</div>
                      <div>Status</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr', padding: '16px 16px', fontSize: 13, color: '#111827', background: '#fff' }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div style={{ fontWeight: 700 }}>1</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                      </div>
                      <div style={{ display: 'grid', gap: 4 }}>
                        <div style={{ fontWeight: 700 }}>{receiptData['Transaction ID'] || '—'}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>IMPS/UTR No: {receiptData['Transaction ID'] || '—'}</div>
                      </div>
                      <div style={{ fontWeight: 700 }}>{receiptData['Amount'] || '—'}</div>
                      <div style={{ fontWeight: 700, color: receiptData['Status']?.toLowerCase() === 'success' ? '#15803d' : '#a32d2d' }}>{receiptData['Status'] || '—'}</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '16px 16px', background: '#f8fafc', fontSize: 13, color: '#111827' }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Request Amount</div>
                        <div style={{ fontWeight: 700 }}>{receiptData['Request Amount'] || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Success Amount</div>
                        <div style={{ fontWeight: 700 }}>{receiptData['Amount'] || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Service Fee</div>
                        <div style={{ fontWeight: 700 }}>{receiptData['Service Fee'] || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>Total Payable</div>
                        <div style={{ fontWeight: 700 }}>{receiptData['Total Payable'] || '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '0 24px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, borderTop: '1px solid #d1d5db' }}>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>Created By:</div>
                    <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.6 }}>
                      S M TELECOM, Email: smcpro@gmail.com, Phone: 9619151713, Tel:, Address: vartak nagar, City: Thane, State: Maharashtra, Pincode: 400606, Country: India.
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: 16, justifyContent: 'end' }}>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>Customer Sign:</div>
                      <div style={{ height: 30, borderBottom: '1px solid #d1d5db' }} />
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>Authorised By:</div>
                      <div style={{ height: 30, borderBottom: '1px solid #d1d5db' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }} className="receipt-no-print">
              <button onClick={() => window.print()} style={styles.secondaryBtn} className="receipt-no-print">
                📄 Print
              </button>
              <button onClick={handleDownloadPdf} style={styles.primaryBtn} className="receipt-no-print">
                ⬇️ Download PDF
              </button>
              <button onClick={() => {
                setShowReceipt(false);
                setSuccessMsg(null);
              }} style={styles.ghostBtn} className="receipt-no-print">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddBeneficiary && (
        <div style={styles.modalOverlay}>
          <form onSubmit={submitAddBeneficiary} style={styles.modal}>
            <h3 style={{ marginTop: 0 }}>Add Beneficiary</h3>
            <input
              placeholder="Account holder name"
              value={newBeneficiary.account_holder_name}
              onChange={(e) => setNewBeneficiary({ ...newBeneficiary, account_holder_name: e.target.value })}
              style={styles.input}
            />
            <select
              value={newBeneficiary.bank_id}
              onChange={(e) => {
                const selectedBank = bankOptions.find((bank) => String(bank.bank_id) === e.target.value);
                setNewBeneficiary({
                  ...newBeneficiary,
                  bank_id: e.target.value,
                  bank_name: selectedBank?.bank_name || "",
                  ifsc_code: selectedBank?.ifsc_code || "",
                });
              }}
              style={styles.input}
            >
              <option value="">Choose bank</option>
              {bankOptions.map((bank) => (
                <option key={bank.bank_id} value={bank.bank_id}>
                  {bank.bank_name}
                </option>
              ))}
            </select>
            <input
              placeholder="IFSC code"
              value={newBeneficiary.ifsc_code}
              readOnly
              style={{ ...styles.input, background: "#f8fafc" }}
            />
            <input
              placeholder="Account number"
              value={newBeneficiary.account_number}
              onChange={(e) => setNewBeneficiary({ ...newBeneficiary, account_number: e.target.value })}
              style={styles.input}
            />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button type="submit" disabled={creating} style={styles.primaryBtn}>{creating ? "Creating..." : "Create"}</button>
              <button type="button" onClick={() => setShowAddBeneficiary(false)} style={styles.ghostBtn}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {showAddCustomer && (
        <div style={styles.modalOverlay} onClick={() => setShowAddCustomer(false)}>
          <form onSubmit={submitAddCustomer} style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Add Customer</h3>
            <input
              placeholder="Mobile number"
              value={newCustomer.mobile_number}
              onChange={(e) => setNewCustomer({ ...newCustomer, mobile_number: e.target.value })}
              style={styles.input}
            />
            <input
              placeholder="Customer name"
              value={newCustomer.customer_name}
              onChange={(e) => setNewCustomer({ ...newCustomer, customer_name: e.target.value })}
              style={styles.input}
            />
            <input
              placeholder="Pincode"
              value={newCustomer.pincode}
              onChange={(e) => setNewCustomer({ ...newCustomer, pincode: e.target.value })}
              style={styles.input}
            />
            {customerFormError && <div style={{ color: "#d9534f", marginTop: 12 }}>{customerFormError}</div>}
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button type="submit" disabled={creatingCustomer} style={{ ...styles.primaryBtn, pointerEvents: "auto" }}>
                {creatingCustomer ? "Creating..." : "Create"}
              </button>
              <button type="button" onClick={() => setShowAddCustomer(false)} style={styles.ghostBtn}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  card: {
    background: "#fff",
    borderRadius: 10,
    padding: 16,
    boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
  },
  cardHeader: {
    fontSize: 16,
    fontWeight: 700,
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: 8,
  },
  input: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e6e6e6",
    outline: "none",
  },
  primaryBtn: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
  },
  ghostBtn: {
    background: "transparent",
    color: "#2563eb",
    border: "1px solid #e6f0ff",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "#eef2ff",
    color: "#1d4ed8",
    border: "none",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "#6b7280",
    fontSize: 18,
    cursor: "pointer",
    padding: 6,
  },
  searchInput: {
    width: "40%",
    minWidth: 0,
    fontSize: 18,
    padding: "14px 16px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  infoBox: {
    marginTop: 12,
    padding: 12,
    background: "#fafafa",
    borderRadius: 8,
  },
  beneficiaryCard: {
    padding: 14,
    borderRadius: 10,
    background: "#fff",
    boxShadow: "0 4px 10px rgba(15,23,42,0.04)",
    position: "relative",
  },
  modalOverlay: {
    position: "fixed",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    background: "#fff",
    padding: 20,
    width: 420,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
};
